import { useCallback, useEffect, useMemo, useState } from 'react';

import { bindingsOf, needsChoice, requirementsOf, startingChoices, stillToChoose, stillToChooseLine } from '../utils/packageBindings';

/**
 * The install dialog's *What it needs* state (keepr-api KPR-1811; plan v4
 * §5.12): which pane shows, the person's choices per requirement, and the
 * `bindings` they make. Choices start from the preview the dialog already
 * asks; once the person is choosing, every preview and the install itself
 * send them. `resetKey` (the collection and the add-ons ticked) starts the
 * choosing over: a different collection has different cards.
 *
 * `forecast` is the dialog's `{ status, data }`. What it reads is the latest
 * preview that answered, kept while the next one loads, so the pane never
 * blanks and the button never flips back in between.
 */
export default function useInstallNeeds({ open, resetKey, forecast }) {
	const [step, setStep] = useState('install');
	const [choices, setChoices] = useState(null);
	// The latest preview that answered, kept while the next one loads.
	const [last, setLast] = useState(null);

	useEffect(() => {
		setStep('install');
		setChoices(null);
		setLast(null);
	}, [open, resetKey]);

	const data = forecast.status === 'ready' ? forecast.data : null;
	useEffect(() => {
		if (data) setLast(data);
	}, [data]);
	const requirements = useMemo(() => requirementsOf(last), [last]);

	const choosing = choices !== null;
	// Asked only of the preview made with no choices: once choosing, the pane is the answer.
	const needs = !choosing && Boolean(last) && needsChoice(last);
	const bindings = useMemo(() => (choosing ? bindingsOf(requirements, choices) : null), [choosing, requirements, choices]);
	const sent = bindings && Object.keys(bindings).length ? bindings : undefined;
	const left = useMemo(() => (choosing ? stillToChoose(requirements, choices) : null), [choosing, requirements, choices]);

	const enter = useCallback(() => {
		setChoices(current => current || startingChoices(last));
		setStep('needs');
	}, [last]);
	const back = useCallback(() => setStep('install'), []);
	const setChoice = useCallback((local, next) => setChoices(current => ({ ...(current || {}), [local]: next })), []);

	return {
		step,
		needs,
		choosing,
		choices: choices || {},
		requirements,
		// The body's `bindings`, JSON-stable for a preview's dependency list.
		bindings: sent,
		bindingsKey: sent ? JSON.stringify(sent) : '',
		leftLine: stillToChooseLine(left),
		enter,
		back,
		setChoice,
	};
}
